import React, {memo, useState, useCallback, useEffect, useRef, useMemo} from 'react';
import Image, {type StaticImageData} from 'next/image';
import parse from 'html-react-parser';
import Container from '@/components/atoms/Container';
import Eyebrow from '@/components/atoms/Eyebrow';
import Icon from '@/components/atoms/Icon';
import {colors, fontSize} from '@/styles/tokens';
import {useAutoplayAllowed} from '@/hooks/useAutoplayAllowed';
import styles from './LeadershipInsights.module.scss';

// ─── Types ────────────────────────────────────────────────────────────────────

interface LeaderQuote {
    image: string | StaticImageData;
    name: string;
    designation: string;
    quote: string;
    linkedinUrl?: string;
}

export interface LeadershipInsightsProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    quotes: LeaderQuote[];
}

const imageFitStyle = {objectFit: 'cover', objectPosition: 'center top'} as const;
const arrowIconStyle: React.CSSProperties = {fontSize: fontSize[13]};

const LinkedInGlyph = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill={colors.text.inverse} aria-hidden="true" focusable="false">
        <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S.02 4.88.02 3.5C.02 2.12 1.13 1 2.5 1s2.48 1.12 2.48 2.5zM.5 8.5h4V24h-4V8.5zm6.5 0h3.8v2.13h.05c.53-1 1.82-2.05 3.75-2.05 4.01 0 4.75 2.64 4.75 6.07V24h-4v-8.56c0-2.04-.04-4.66-2.84-4.66-2.85 0-3.28 2.22-3.28 4.51V24H7V8.5z" />
    </svg>
);

// ─── Main component ───────────────────────────────────────────────────────────

const AUTOPLAY_INTERVAL_MS = 5000;
const RESUME_AFTER_MS = 15000;
const FADE_MS = 250;
const SWIPE_THRESHOLD = 50;

const LeadershipInsights = ({eyebrow, heading, subheading, quotes}: LeadershipInsightsProps) => {
    const total = quotes.length;

    const [active, setActive] = useState(0);
    const [visible, setVisible] = useState(true);
    const [isPaused, setIsPaused] = useState(false);
    const [inView, setInView] = useState(false);
    const sectionRef = useRef<HTMLElement | null>(null);
    const touchStartX = useRef(0);
    const activeRef = useRef(0);
    const fadeTimer = useRef<ReturnType<typeof setTimeout>>();
    const resumeTimer = useRef<ReturnType<typeof setTimeout>>();

    // Interacting pauses autoplay, but only for a while — otherwise one arrow click stops
    // the carousel for the rest of the visit.
    const pauseAutoplay = useCallback(() => {
        setIsPaused(true);
        if (resumeTimer.current) clearTimeout(resumeTimer.current);
        resumeTimer.current = setTimeout(() => setIsPaused(false), RESUME_AFTER_MS);
    }, []);

    const goTo = useCallback(
        (next: number) => {
            if (total === 0) return;
            const target = ((next % total) + total) % total;
            if (target === activeRef.current) return;
            activeRef.current = target;
            setVisible(false);
            if (fadeTimer.current) clearTimeout(fadeTimer.current);
            fadeTimer.current = setTimeout(() => {
                setActive(target);
                setVisible(true);
            }, FADE_MS);
        },
        [total]
    );

    useEffect(
        () => () => {
            if (fadeTimer.current) clearTimeout(fadeTimer.current);
            if (resumeTimer.current) clearTimeout(resumeTimer.current);
        },
        []
    );

    const advance = useCallback(
        (delta: number) => {
            if (total <= 1) return;
            goTo(activeRef.current + delta);
        },
        [goTo, total]
    );

    const handlePrev = useCallback(() => {
        pauseAutoplay();
        advance(-1);
    }, [advance, pauseAutoplay]);

    const handleNext = useCallback(() => {
        pauseAutoplay();
        advance(1);
    }, [advance, pauseAutoplay]);

    // Only autoplay while the section is on screen.
    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return undefined;
        const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {threshold: 0.25});
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Desktop only: on phones the quotes and names wrap to different heights, so each rotation moved
    // the page below without any input (layout shift). Swipe and arrows still change the leader there.
    const autoplayAllowed = useAutoplayAllowed(sectionRef);

    useEffect(() => {
        if (!autoplayAllowed || isPaused || !inView || total <= 1) return undefined;
        const id = setInterval(() => advance(1), AUTOPLAY_INTERVAL_MS);
        return () => clearInterval(id);
    }, [autoplayAllowed, isPaused, inView, total, advance]);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
    }, []);

    const handleTouchEnd = useCallback(
        (e: React.TouchEvent) => {
            const delta = touchStartX.current - e.changedTouches[0].clientX;
            if (Math.abs(delta) < SWIPE_THRESHOLD) return;
            pauseAutoplay();
            advance(delta > 0 ? 1 : -1);
        },
        [advance, pauseAutoplay]
    );

    const current = quotes[active];
    const progressPct = useMemo(() => (total ? ((active + 1) / total) * 100 : 0), [active, total]);

    if (!current) return null;

    // Crossfade on swap — no carousel track, so a long quote never jitter-slides.
    const fadeClass = visible ? styles.fade : `${styles.fade} ${styles.fadeOut}`;

    return (
        <section ref={sectionRef} className={`top-space mobile-top-padding bottom-spacing mobile-padding-bottom ${styles.section}`}>
            <Container>
                <div className={styles.containerInner}>
                    <div className={styles.headingBlock}>
                        {eyebrow && <Eyebrow text={eyebrow} className={styles.eyebrow} />}
                        <h2 className={`font-headers ${styles.heading}`}>{heading}</h2>
                        {subheading && <p className={`font-regular-text ${styles.subheading}`}>{parse(subheading)}</p>}
                    </div>

                    <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                        <div className={styles.grid}>
                            <div className={`${styles.item} ${styles.portraitCol}`}>
                                <div className={fadeClass}>
                                    <div className={styles.portraitFrame}>
                                        <Image
                                            src={current.image}
                                            alt={current.name}
                                            fill
                                            style={imageFitStyle}
                                            sizes="(max-width: 900px) 240px, (max-width: 1500px) 230px, 280px"
                                            unoptimized
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className={`${styles.item} ${styles.quoteCol}`}>
                                <div className={styles.quoteColumn}>
                                    <div className={fadeClass}>
                                        <span className={styles.quoteMark} aria-hidden="true">
                                            &#8220;
                                        </span>
                                        <blockquote className={styles.blockquote}>
                                            <p className={`font-regular-text ${styles.quoteText}`}>{current.quote}</p>
                                        </blockquote>
                                        <div className={styles.attributionRow}>
                                            <span className={`font-headers ${styles.attributionName}`}>{current.name}</span>
                                            <span className={`font-regular-text ${styles.attributionRole}`}>{current.designation}</span>
                                            {current.linkedinUrl && (
                                                <a
                                                    href={current.linkedinUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label={`${current.name} on LinkedIn`}
                                                    className={styles.linkedinButton}
                                                >
                                                    <LinkedInGlyph />
                                                </a>
                                            )}
                                        </div>
                                    </div>

                                    {total > 1 && (
                                        <div className={styles.controlsRow}>
                                            <button
                                                type="button"
                                                onClick={handlePrev}
                                                aria-label="Previous insight"
                                                className={styles.arrowButton}
                                            >
                                                <Icon name="ArrowBackIosNew" style={arrowIconStyle} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleNext}
                                                aria-label="Next insight"
                                                className={styles.arrowButton}
                                            >
                                                <Icon name="ArrowForwardIos" style={arrowIconStyle} />
                                            </button>
                                            <span className={`font-headers ${styles.counter}`}>
                                                {String(active + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
                                            </span>
                                            <div className={styles.progressTrack}>
                                                <span className={styles.progressFill} style={{width: `${progressPct}%`}} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Container>
        </section>
    );
};

export default memo(LeadershipInsights);
